<script setup lang="ts">
import { computed, ref } from 'vue'

import { useReportCellFormatter } from '../../composables/useReportCellFormatter'
import type { ReportColumn } from '../../models/column.types'
import type { TrendTableRow } from '../../models/trendTable.types'
import { toTableColumns } from '../../service/toTableColumns'
import { buildTrendTablePresentation } from '../../service/trendTablePresentation'

import type { TrendTableProps } from './types'

import { useI18n } from '@/shared/i18n'
import { DataTable, type DataTableProps } from '@/shared/ui'
import { MetricView } from '@/shared/ui/metric-view'

const props = withDefaults(defineProps<TrendTableProps>(), { state: 'ready', channel: '' })
const emit = defineEmits<{ 'load-more': [] }>()
defineSlots<{
  empty?: () => unknown
  error?: (props: { retry: () => void }) => unknown
}>()

const { t } = useI18n()
const { formatCell } = useReportCellFormatter()
const expandedKeys = ref(new Set<string>())
const presentation = computed(() =>
  buildTrendTablePresentation({
    data: props.data,
    columns: props.columns,
    breakdownColumn: props.breakdownColumn,
    previewColumn: props.previewColumn,
    dateDimensions: props.dateDimensions,
    frequency: props.frequency,
  }),
)
const tableColumns = computed(() => toTableColumns<TrendTableRow>(presentation.value.columns))
const metricColumns = computed(
  () =>
    new Map(
      [...props.columns, props.previewColumn].map((column) => [column.field, column] as const),
    ),
)

function rowKey(row: TrendTableRow): string {
  return row.__trendKey
}

function childrenFor(row: TrendTableRow): TrendTableRow[] {
  return row.__trendChildren
}

function rowLabel(row: TrendTableRow): string {
  return formatCell(row.__trendLabel, props.breakdownColumn)
}

function expandLabel(row: TrendTableRow): string {
  return t('reports.table.expandBreakdown', { value: rowLabel(row) })
}

function collapseLabel(row: TrendTableRow): string {
  return t('reports.table.collapseBreakdown', { value: rowLabel(row) })
}

function displayLabel(row: TrendTableRow): string {
  return row.__trendKind === 'metric' ? String(row.__trendLabel) : rowLabel(row)
}

function displayValue(row: TrendTableRow, value: unknown): string {
  if (row.__trendKind === 'summary' && expandedKeys.value.has(row.__trendKey)) return ''
  const column = metricColumns.value.get(row.__trendMetricField)
  return column ? formatCell(value, column) : ''
}

function onExpand(row: TrendTableRow, expanded: boolean): void {
  const next = new Set(expandedKeys.value)
  if (expanded) next.add(row.__trendKey)
  else next.delete(row.__trendKey)
  expandedKeys.value = next
}

function bucketColumn(field: string): ReportColumn | undefined {
  return presentation.value.columns.find((column) => column.field === field)
}

const dataTableProps = computed<DataTableProps<TrendTableRow>>(() => ({
  rows: presentation.value.rows,
  columns: tableColumns.value,
  ariaLabel: props.ariaLabel,
  state: props.state,
  skeletonRows: 10,
  refreshing: props.refreshing,
  rowKey,
  getChildren: childrenFor,
  expandLabel,
  collapseLabel,
  expandable: true,
  clientSort: false,
  interactiveRows: false,
  ...(props.maxHeight === undefined ? {} : { maxHeight: props.maxHeight }),
  ...(props.totalRows === undefined ? {} : { totalRows: props.totalRows }),
}))
</script>

<template>
  <DataTable v-bind="dataTableProps" @expand="onExpand" @load-more="emit('load-more')">
    <template #empty><slot name="empty" /></template>
    <template #error="slotProps"><slot name="error" v-bind="slotProps" /></template>
    <template #cell-__trendLabel="{ row }">
      <MetricView
        v-if="row.__trendKind === 'metric'"
        :field="row.__trendMetricField"
        :column="metricColumns.get(row.__trendMetricField)!"
        :channel="channel"
      />
      <template v-else>{{ displayLabel(row) }}</template>
    </template>
    <template
      v-for="field in presentation.bucketFields"
      :key="field"
      #[`cell-${field}`]="{ row, value }"
    >
      <span :data-trend-bucket="bucketColumn(field)?.meta?.trendBucket">
        {{ displayValue(row, value) }}
      </span>
    </template>
  </DataTable>
</template>
